/**
 * CriticalMonitoringSection — Iter-32 Phase 4.
 *
 * Renders the classified critical-URL checks from the every-5-min sweep as
 * a distinct section above the daily audit. Consumes customer-facing
 * labels from `useScanLabels()` (Iter-32 Phase 3b) — the terminology pass
 * post-Phase-4 will edit `services/scan_labels.py` and this component
 * will pick up the new strings automatically on next page load.
 *
 * A check belongs to this section if:
 *   • its `key` is `host_unreachable`, OR
 *   • its `key` starts with `critical:` (per-URL classified checks).
 *
 * States rendered:
 *   • passing              — green dot, no expander
 *   • verification_pending — amber dot, "we're still confirming"
 *   • monitoring_blocked   — amber dot, "scanner interference"
 *   • confirmed_failure    — red dot, escalated
 *   • not_checked          — muted dot, "dependent check skipped"
 *
 * A developer-details expander shows `developer_detail` verbatim (raw
 * HTTP status / error kind / probe outcome) — hidden by default so
 * customers never see raw Python errors.
 */
import { useState } from "react";
import {
  CheckCircle2, Clock, Shield, XCircle, MinusCircle,
} from "lucide-react";
import { useScanLabels } from "../hooks/useScanLabels";
import DeveloperDetails from "./DeveloperDetails";

// State → { dot color, icon, tone } — visual tokens only. All copy comes
// from `useScanLabels().states(code)` so a rebrand is one file diff.
const STATE_VISUAL = {
  passing: {
    dot: "bg-emerald-500",
    ring: "ring-emerald-100",
    icon: CheckCircle2,
    iconColor: "text-emerald-500",
    tone: "text-emerald-700",
  },
  verification_pending: {
    dot: "bg-amber-500",
    ring: "ring-amber-100",
    icon: Clock,
    iconColor: "text-amber-500",
    tone: "text-amber-700",
  },
  monitoring_blocked: {
    dot: "bg-amber-500",
    ring: "ring-amber-100",
    icon: Shield,
    iconColor: "text-amber-500",
    tone: "text-amber-700",
  },
  confirmed_failure: {
    dot: "bg-red-500",
    ring: "ring-red-100",
    icon: XCircle,
    iconColor: "text-red-500",
    tone: "text-red-700",
  },
  not_checked: {
    dot: "bg-charcoal-300",
    ring: "ring-charcoal-100",
    icon: MinusCircle,
    iconColor: "text-charcoal-400",
    tone: "text-charcoal-500",
  },
};

function CriticalCheckRow({ check, labels }) {
  const state = check.state || "passing";
  const v = STATE_VISUAL[state] || STATE_VISUAL.passing;
  const Icon = v.icon;
  const stateLabel = labels.states(state);
  return (
    <div
      className="flex items-start gap-3 py-3 px-3.5 rounded-xl border border-black/5 bg-white"
      data-testid={`critical-check-${check.key}`}
      data-state={state}
    >
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${v.iconColor}`} strokeWidth={1.75} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <p className="text-sm text-charcoal-800 leading-tight font-medium">
            {check.label}
          </p>
          <span
            className={`text-[10px] font-mono uppercase tracking-wider ${v.tone}`}
            data-testid={`critical-check-state-${check.key}`}
          >
            {stateLabel}
          </span>
        </div>
        {check.url && (
          <a
            href={check.url}
            target="_blank"
            rel="noreferrer"
            className="mt-0.5 block text-[11px] font-mono text-charcoal-500 hover:text-brand hover:underline break-all"
            data-testid={`critical-check-url-${check.key}`}
          >
            {check.url}
          </a>
        )}
        {check.detail && (
          <p className="mt-1.5 text-[12.5px] text-charcoal-600 leading-snug">
            {check.detail}
          </p>
        )}
        <DeveloperDetails text={check.developer_detail} testIdSuffix={check.key} />
      </div>
      <span className={`mt-2 w-2 h-2 rounded-full ${v.dot} ring-4 ${v.ring} shrink-0`} />
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array}  props.checks — the FULL report.checks array; this
 *                                component filters to the critical set.
 * @param {string} [props.fastSweepAt] — ISO string of the last fast sweep;
 *                                       shown as "checked N min ago" if present.
 */
export default function CriticalMonitoringSection({ checks, fastSweepAt }) {
  const labels = useScanLabels();
  if (!Array.isArray(checks)) return null;

  const critical = checks.filter(
    (c) => c && (c.key === "host_unreachable" || (typeof c.key === "string" && c.key.startsWith("critical:"))),
  );
  if (!critical.length) return null;

  // Sort: host incident first, then confirmed_failure > verification_pending
  //  > monitoring_blocked > not_checked > passing.
  const STATE_ORDER = {
    confirmed_failure: 0, verification_pending: 1,
    monitoring_blocked: 2, not_checked: 3, passing: 4,
  };
  const sorted = [...critical].sort((a, b) => {
    if (a.key === "host_unreachable") return -1;
    if (b.key === "host_unreachable") return 1;
    return (STATE_ORDER[a.state] ?? 5) - (STATE_ORDER[b.state] ?? 5);
  });

  const tierLabel = labels.tiers("critical");

  // Small "N min ago" for the last fast sweep so customers see the
  // section is fresh.
  const ago = (() => {
    if (!fastSweepAt) return null;
    try {
      const then = new Date(fastSweepAt).getTime();
      if (Number.isNaN(then)) return null;
      const min = Math.max(0, Math.round((Date.now() - then) / 60000));
      if (min < 1) return "just now";
      if (min < 60) return `${min} min ago`;
      return `${Math.round(min / 60)}h ago`;
    } catch {
      return null;
    }
  })();

  return (
    <section
      className="mb-6 rounded-xl border border-black/5 bg-charcoal-50/50 p-4 sm:p-5"
      data-testid="critical-monitoring-section"
    >
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full bg-brand" />
          <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-charcoal-700 truncate">
            {tierLabel}
          </p>
        </div>
        {ago && (
          <span
            className="text-[10.5px] font-mono text-charcoal-400"
            data-testid="critical-monitoring-last-checked"
          >
            checked {ago}
          </span>
        )}
      </div>
      <div className="space-y-2">
        {sorted.map((c) => (
          <CriticalCheckRow key={c.key} check={c} labels={labels} />
        ))}
      </div>
    </section>
  );
}
